import { useEffect, useRef, useState } from "react";
import { Bell, Clock, AlertTriangle, ShieldCheck, ShieldX, CheckCheck, Sparkles, Rocket, Send, FileText } from "lucide-react";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";
import TaskDetailModal from "./TaskDetailModal";
import ContentDetailModal from "./ContentDetailModal";

const ICON = {
  task_submitted_done: Clock,
  task_blocked: AlertTriangle,
  task_verified_done: ShieldCheck,
  task_rejected_done: ShieldX,
  content_assigned: Rocket,
  content_brief_ready: Sparkles,
  content_awaiting_approval: Send,
  content_revision: AlertTriangle,
  content_approved: ShieldCheck,
  content_published: FileText,
};
const COLOR = {
  task_submitted_done: "text-amber-600",
  task_blocked: "text-rose-600",
  task_verified_done: "text-emerald-600",
  task_rejected_done: "text-orange-600",
  content_assigned: "text-brand",
  content_brief_ready: "text-brand",
  content_awaiting_approval: "text-amber-600",
  content_revision: "text-orange-600",
  content_approved: "text-emerald-600",
  content_published: "text-emerald-600",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [contentDetailId, setContentDetailId] = useState(null);
  const ref = useRef(null);

  const loadCount = async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setCount(data.count || 0); }
    catch (e) { /* ignore */ }
  };
  const loadItems = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data || []); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 45000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };
  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); }
      catch (e) { /* ignore */ }
    }
    setOpen(false);
    setCount((c) => Math.max(0, c - (n.read ? 0 : 1)));
    if (n.task_id) setDetailId(n.task_id);
    else if (n.content_id) setContentDetailId(n.content_id);
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems((arr) => arr.map((x) => ({ ...x, read: true })));
      setCount(0);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative p-2 rounded-md hover:bg-[#FAF6EE] transition" data-testid="btn-notifications" aria-label="Notifikasi">
        <Bell className="w-5 h-5 text-[#3C4045]" />
        {count > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notif-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] max-w-[90vw] bg-white border border-[#E5E0D8] rounded-md shadow-lg z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="px-4 py-2.5 flex items-center justify-between border-b border-[#EAE3D6]">
            <div className="text-sm font-semibold">Notifikasi</div>
            {items.some((x) => !x.read) && (
              <button onClick={markAll} className="text-[11px] text-brand hover:underline flex items-center gap-1" data-testid="btn-mark-all-read">
                <CheckCheck className="w-3.5 h-3.5"/>Tandai semua dibaca
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 && <div className="p-6 text-center text-sm text-[#8C9096]">Tidak ada notifikasi</div>}
            {items.map((n) => {
              const Icon = ICON[n.type] || Bell;
              const col = COLOR[n.type] || "text-[#3C4045]";
              return (
                <button key={n.id} onClick={() => openNotif(n)}
                  className={`w-full text-left px-4 py-3 border-t border-[#F0EBE0] hover:bg-[#FAF6EE] flex items-start gap-2.5 ${!n.read ? "bg-[#FDFAF3]" : ""}`}
                  data-testid={`notif-item-${n.id}`}>
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${col}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{n.message}</div>
                    <div className="text-[11px] text-[#8C9096] mt-0.5">{formatDate(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-rose-500 mt-1.5 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <TaskDetailModal
        taskId={detailId}
        open={!!detailId}
        onOpenChange={(v) => { if (!v) { setDetailId(null); loadCount(); } }}
      />
      <ContentDetailModal
        contentId={contentDetailId}
        open={!!contentDetailId}
        onOpenChange={(v) => { if (!v) { setContentDetailId(null); loadCount(); } }}
      />
    </div>
  );
}
